import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Hourglass, ArrowSquareOut, Package } from "@phosphor-icons/react";
import { convert, money, compact } from "./tiersShared";

export const AGE_COLORS = { "0_30": "#00873D", "31_60": "#FFC000", "61_90": "#FF7043", "90_plus": "#E0000F" };
const AGE_MIN_DAYS = { "0_30": 0, "31_60": 31, "61_90": 61, "90_plus": 91 };

function AgingTooltip({ active, payload, label, metric, cur }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + Number(p.value || 0), 0);
  return (
    <div className="bg-white border border-zinc-200 shadow-md p-3 text-xs min-w-[200px]">
      <div className="font-semibold text-zinc-900 mb-1.5">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center justify-between gap-4 py-0.5">
          <span className="inline-flex items-center gap-1.5 text-zinc-600"><span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />{p.name}</span>
          <span className="numeric font-semibold">{metric === "items" ? `${p.value} pz` : money(p.value, cur)}</span>
        </div>
      ))}
      <div className="border-t border-zinc-100 mt-1.5 pt-1.5 flex justify-between text-zinc-700"><span>Total</span><span className="numeric font-semibold">{metric === "items" ? `${total} pz` : money(total, cur)}</span></div>
    </div>
  );
}

export default function StockAgingCard({ categories, agingTotals, buckets, stagnant, cur, rate }) {
  const [metric, setMetric] = useState("items");
  const key = metric === "items" ? "items" : "store_usd";

  const data = useMemo(() => categories.map((c) => {
    const row = { name: c.name };
    buckets.forEach((b) => { row[b.key] = metric === "items" ? (c.aging?.[b.key]?.items || 0) : Math.round(convert(c.aging?.[b.key]?.store_usd || 0, cur, rate)); });
    return row;
  }), [categories, buckets, metric, cur, rate]);

  const grand = buckets.reduce((a, b) => a + Number(agingTotals?.[b.key]?.[key] || 0), 0);
  const stagnantCount = stagnant.length;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4" data-testid="stock-aging-block">
      <div className="xl:col-span-8 border border-zinc-200 bg-white p-4" data-testid="stock-aging-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5">
              <Hourglass size={14} weight="bold" className="text-[#002FA7]" /> Antigüedad del stock por categoría
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Días desde el alta del producto · detecta inventario estancado</div>
          </div>
          <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden bg-white text-xs" data-testid="aging-metric">
            {[{ v: "items", l: "Piezas" }, { v: "value", l: "Valor tienda" }].map((o) => (
              <button key={o.v} type="button" onClick={() => setMetric(o.v)}
                className={`px-3 py-1.5 font-semibold transition-colors ${metric === o.v ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`aging-metric-${o.v}`}>{o.l}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3" data-testid="aging-summary">
          {buckets.map((b) => {
            const v = Number(agingTotals?.[b.key]?.[key] || 0);
            const pct = grand > 0 ? (v / grand) * 100 : 0;
            return (
              <Link key={b.key} to={`/products?age=${AGE_MIN_DAYS[b.key]}`} className="border rounded-md p-2.5 hover:shadow-sm transition-shadow block"
                style={{ borderColor: `${AGE_COLORS[b.key]}55`, background: `${AGE_COLORS[b.key]}0d` }} data-testid={`aging-bucket-${b.key}`}
                title="Ver estos productos en Inventario">
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: AGE_COLORS[b.key] }}>{b.label}</div>
                <div className="numeric font-bold text-lg text-zinc-900 mt-0.5">{metric === "items" ? `${v} pz` : compact(convert(agingTotals?.[b.key]?.store_usd || 0, cur, rate), cur)}</div>
                <div className="text-[11px] text-zinc-500 numeric">{pct.toFixed(0)}% · {agingTotals?.[b.key]?.products || 0} productos</div>
              </Link>
            );
          })}
        </div>

        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 8 }} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e4e4e7" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#52525b" }} axisLine={false} tickLine={false} interval={0} />
              <YAxis tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} width={metric === "items" ? 40 : 64}
                tickFormatter={(v) => (metric === "items" ? v : compact(v, cur))} />
              <Tooltip cursor={{ fill: "#f4f4f5" }} content={<AgingTooltip metric={metric} cur={cur} />} />
              {buckets.map((b, i) => (
                <Bar key={b.key} dataKey={b.key} name={b.label} stackId="age" fill={AGE_COLORS[b.key]} maxBarSize={56}
                  radius={i === buckets.length - 1 ? [3, 3, 0, 0] : 0} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="xl:col-span-4 border border-zinc-200 bg-white p-4 flex flex-col" data-testid="stagnant-card">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#E0000F]" /> Más estancados (+90 días)
          </div>
          <Link to="/products?age=91" className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid="stagnant-see-all">
            Ver todos <ArrowSquareOut size={11} />
          </Link>
        </div>
        <div className="text-[11px] text-zinc-500 mb-2">Prioriza estos equipos para promociones o lotes.</div>
        {stagnantCount === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-xs text-zinc-400 py-8" data-testid="stagnant-empty">
            <Package size={24} className="text-zinc-300 mb-1" /> Nada estancado: todo el stock tiene menos de 90 días.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100 text-xs" data-testid="stagnant-list">
            {stagnant.map((p) => (
              <li key={p.id} className="py-2 flex items-center gap-2.5">
                <span className="w-9 h-9 rounded bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center shrink-0">
                  {p.photo ? <img src={p.photo} alt="" className="w-full h-full object-cover" /> : <Package size={14} className="text-zinc-300" />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-medium text-zinc-900 truncate" title={p.name}>{p.name}</span>
                  <span className="block text-[10px] text-zinc-500 truncate">{p.category} · {p.brand} · {p.stock} pz</span>
                </span>
                <span className="text-right shrink-0">
                  <span className="block numeric font-bold text-[#E0000F]">{p.age_days} d</span>
                  <span className="block text-[10px] text-zinc-500 numeric">{compact(convert(p.store_usd, cur, rate), cur)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
